import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector, useDispatch } from 'react-redux';
import { getComplex } from 'src/store/crosstable/selectors';
import { Actions } from 'src/store/crosstable/actions';
import { getComplexData } from 'src/api';
import { useGetAuthData } from 'src/hooks/use-get-auth-data';
import { IComplex } from '../typings/complexes';

export const useGetComplex = (id: string|undefined|null, saveToStore?: boolean) => {
  const savedComplex: IComplex|null = useSelector(getComplex);
  const dispatch = useDispatch();
  const { getJWT } = useGetAuthData();
  const jwt = getJWT();

  const { data: complex, isLoading, refetch: fetchComplex } = useQuery({
    queryKey: ['complexes', id],
    queryFn: async (): Promise<IComplex | null> => {
      const response = await getComplexData(jwt, id!);
      return response.data;
    },
    // String-safe compare: complex ids are UID strings — Number(uid) is NaN and
    // NaN !== NaN made this query ALWAYS enabled, so every mount (e.g. opening
    // the unit sidebar) re-fetched the complex and re-dispatched setComplex,
    // resetting the app language and the selected placement type.
    enabled: !!id && (!savedComplex || String(id) !== String(savedComplex.id)),
    initialData: savedComplex && String(id) === String(savedComplex.id)
      ? savedComplex
      : undefined,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
  });

  useEffect(() => {
    const shouldSaveToStore = saveToStore === undefined || saveToStore;
    if (shouldSaveToStore && complex && String(savedComplex?.id) !== String(complex.id)) {
      dispatch(Actions.setComplex(complex));
    }
  }, [complex, dispatch, saveToStore, savedComplex?.id]);

  const isSavedComplexActual = !id
    || (savedComplex && String(id) === String(savedComplex.id));

  return {
    isLoading,
    complex: complex || (isSavedComplexActual ? savedComplex : null),
    fetchComplex,
  };
};
